// Factor review with drill-down. Each row expands to show the months the factor was
// actually built from and how the same combination has moved across runs — the "why is
// this number what it is" question, answerable without re-running anything.

import { Fragment, useState } from 'react';
import { cmApi } from '../../api.js';
import { useClient } from '../../ClientContext.jsx';
import { fmtCurrency } from '../../format.js';
import { T } from '../../../../shared/styles/tokens.js';

const BAND_CLASS = { high: 'o-ok', medium: 'o-grey', low: 'o-err' };

const dimLabel = (f) => {
  const parts = [f.market, f.category, f.channel_key, f.publisher_key, f.creative_format,
                 f.brand, f.producer, f.month]
    .filter(Boolean);
  return parts.length ? parts.join(' · ') : 'All records';
};

export default function FactorTable({ factors }) {
  const { clientId } = useClient();
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async (f) => {
    if (expanded === f.id) { setExpanded(null); setDetail(null); return; }
    setExpanded(f.id); setDetail(null); setLoading(true);
    try {
      setDetail(await cmApi.getFactorDetail(clientId, f.id));
    } catch (e) {
      setDetail({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="o-panel" style={{ overflowX: 'auto' }}>
      <table className="o-tbl">
        <thead>
          <tr>
            <th style={{ width: 28 }}></th>
            <th>Level</th>
            <th>Combination</th>
            <th>Factor</th>
            <th>Range</th>
            <th>Months</th>
            <th>Window</th>
            <th>Statex</th>
            <th>Actual</th>
            <th>Confidence</th>
          </tr>
        </thead>
        <tbody>
          {factors.length === 0 && (
            <tr><td colSpan={10} style={{ textAlign: 'center', color: T.text3 }}>
              No factors at this level. Records here fall through to a coarser level.
            </td></tr>
          )}
          {factors.map(f => (
            <Fragment key={f.id}>
              <tr onClick={() => toggle(f)} style={{ cursor: 'pointer' }}>
                <td style={{ color: T.text3 }}>{expanded === f.id ? '▾' : '▸'}</td>
                <td><span className="o-badge o-grey">L{f.level_rank}</span></td>
                <td className="o-strong">{dimLabel(f)}</td>
                <td className="o-num">{Number(f.ratio).toFixed(3)}×</td>
                <td className="o-mono" style={{ fontSize: 11, color: T.text3 }}>
                  {Number(f.low_ratio).toFixed(2)} – {Number(f.high_ratio).toFixed(2)}
                </td>
                <td className="o-num">{f.n_points}</td>
                <td style={{ color: T.text3 }}>{f.window_months === 0 ? 'All' : `${f.window_months}m`}</td>
                <td className="o-num">{fmtCurrency(f.statex_spend)}</td>
                <td className="o-num">{fmtCurrency(f.actual_spend)}</td>
                <td>
                  <span className={`o-badge ${BAND_CLASS[f.confidence_band] || 'o-grey'}`}>
                    {f.confidence_band}
                  </span>
                </td>
              </tr>

              {expanded === f.id && (
                <tr>
                  <td colSpan={10} style={{ padding: `${T.s3}px ${T.s4}px ${T.s5}px`, background: T.surface2 }}>
                    {loading && <p className="o-state">Loading…</p>}
                    {detail?.error && <p className="o-state o-error">{detail.error}</p>}
                    {detail?.factor && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: T.s5 }}>
                        <div>
                          <p className="o-eyebrow" style={{ marginBottom: T.s2 }}>
                            Months behind this factor
                          </p>
                          <table className="o-tbl">
                            <thead>
                              <tr><th>Month</th><th>Statex</th><th>Actual</th><th>Ratio</th><th>Used</th></tr>
                            </thead>
                            <tbody>
                              {detail.factor.matched.map(mm => (
                                <tr key={mm.month} style={{ opacity: mm.in_window ? 1 : 0.5 }}>
                                  <td className="o-mono">{String(mm.month).slice(0, 7)}</td>
                                  <td className="o-num">{fmtCurrency(mm.statex_spend)}</td>
                                  <td className="o-num">{fmtCurrency(mm.actual_spend)}</td>
                                  <td className="o-num">{Number(mm.ratio).toFixed(3)}×</td>
                                  <td style={{ color: T.text3 }}>{mm.in_window ? 'yes' : 'outside window'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>

                          <p className="o-eyebrow" style={{ margin: `${T.s4}px 0 ${T.s2}px` }}>
                            How this window was chosen
                          </p>
                          <ul className="o-hint" style={{ margin: 0, paddingLeft: 16 }}>
                            {(detail.factor.trace || []).map((t, i) => (
                              <li key={i}>
                                {t.window_months === 0 ? 'All history' : `${t.window_months} months`}:{' '}
                                {t.n_points} month{t.n_points === 1 ? '' : 's'}
                                {t.accepted ? ' — used' : ` — rejected, ${t.reason}`}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <p className="o-eyebrow" style={{ marginBottom: T.s2 }}>
                            This combination across runs
                          </p>
                          <table className="o-tbl">
                            <thead>
                              <tr><th>Run</th><th>When</th><th>Factor</th><th>Months</th><th>Confidence</th></tr>
                            </thead>
                            <tbody>
                              {(detail.history || []).map(h => (
                                <tr key={h.run_id}>
                                  <td className="o-mono">#{h.run_id}</td>
                                  <td style={{ color: T.text3 }}>{String(h.created_at).slice(0, 10)}</td>
                                  <td className="o-num">{Number(h.ratio).toFixed(3)}×</td>
                                  <td className="o-num">{h.n_points}</td>
                                  <td>
                                    <span className={`o-badge ${BAND_CLASS[h.confidence_band] || 'o-grey'}`}>
                                      {h.confidence_band}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <p className="o-note" style={{ marginTop: T.s3 }}>
                            The range is the spread of the monthly ratios above, widened when a
                            factor comes from a coarser level. It is a plausible band, not a
                            statistical confidence interval.
                          </p>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
